/**
 * PolicyPlayground — public, no-auth Rego playground that lets prospects
 * try the CARTIE Policy Engine without signing up.
 *
 * Flow:
 *   1. Pick a template from /api/policy/templates (5 starters)
 *   2. Edit Rego source + input JSON
 *   3. Click "Test" → POST /api/policy/public-dry-run
 *   4. See {allow, violations, warnings} live
 *
 * Strategic value: developer-attraction marketing surface. Anyone who
 * lands here from a Google search for "OPA Rego FinOps" can be productive
 * in 30 seconds without an account. Funnels to /login at the bottom.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ShieldCheck, Play, Code2, CheckCircle2, XCircle, AlertCircle, Loader2, BookOpen, FileCode, Link2, Check } from 'lucide-react';
import SEOHead from '../components/SEOHead';
// v34.55 — promoted from BackToHomePill → PublicNavbar so visitors get the same top nav as /pilot, /wall-of-tenants, etc.
import PublicNavbar from '../components/PublicNavbar';

const API = process.env.REACT_APP_BACKEND_URL;

// URL-safe base64 (no padding, +/ → -_) for compact share links.
const b64UrlEncode = (s) => btoa(unescape(encodeURIComponent(s))).replace(/=+$/, '').replace(/\+/g, '-').replace(/\//g, '_');
const b64UrlDecode = (s) => {
  try {
    s = s.replace(/-/g, '+').replace(/_/g, '/');
    while (s.length % 4) s += '=';
    return decodeURIComponent(escape(atob(s)));
  } catch {
    return null;
  }
};

export default function PolicyPlayground() {
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [rego, setRego] = useState('');
  const [inputJson, setInputJson] = useState('{}');
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);

  // Parse share-link params on first mount (?t=template_id OR ?r=base64&i=base64)
  const readShareParams = () => {
    const params = new URLSearchParams(window.location.search);
    return {
      tplId: params.get('t'),
      regoB64: params.get('r'),
      inputB64: params.get('i'),
    };
  };

  useEffect(() => {
    document.title = 'Rego Policy Playground · CARTIEAI';
    (async () => {
      try {
        const res = await fetch(`${API}/api/policy/templates`);
        const json = await res.json();
        const tpls = json.templates || [];
        setTemplates(tpls);

        const shared = readShareParams();
        // Priority: raw rego+input from share > named template id > first template
        if (shared.regoB64) {
          const decRego = b64UrlDecode(shared.regoB64);
          const decInput = shared.inputB64 ? b64UrlDecode(shared.inputB64) : '{}';
          if (decRego !== null) {
            setSelectedId(shared.tplId || null);
            setRego(decRego);
            setInputJson(decInput || '{}');
            return;
          }
        }
        if (shared.tplId) {
          const match = tpls.find((t) => t.template_id === shared.tplId);
          if (match) {
            pickTemplate(match, /* updateUrl */ false);
            return;
          }
        }
        if (tpls.length) {
          pickTemplate(tpls[0], /* updateUrl */ false);
        }
      } catch (e) {
        setErr(String(e.message || e));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pickTemplate = (tpl, updateUrl = true) => {
    setSelectedId(tpl.template_id);
    setRego(tpl.rego_source || '');
    setInputJson(JSON.stringify(tpl.sample_input ?? {}, null, 2));
    setResult(null);
    setErr(null);
    if (updateUrl) {
      // Clean URL — just the template id, no big base64 payload
      const next = new URL(window.location.href);
      next.search = '?t=' + encodeURIComponent(tpl.template_id);
      window.history.replaceState(null, '', next.toString());
    }
  };

  const copyShareLink = async () => {
    try {
      // Build a self-contained permalink: encodes the user's current edits
      // so they can paste it into a tweet, HN comment, or DM and the
      // recipient lands on the exact same Rego + input.
      //
      // We route the link through /api/og/policy-share so that social
      // crawlers (Twitter, LinkedIn, Slack, HN) see a server-rendered HTML
      // page with dynamic og:image showing the live verdict. Humans get
      // redirected to /policy-playground?... via meta-refresh + JS.
      const origin = window.location.origin;
      const params = new URLSearchParams();
      if (selectedId) params.set('t', selectedId);
      params.set('r', b64UrlEncode(rego));
      params.set('i', b64UrlEncode(inputJson));
      const shareUrl = `${origin}/api/og/share/policy?${params.toString()}`;
      await navigator.clipboard.writeText(shareUrl);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    } catch {
      setShareCopied(false);
    }
  };

  const runTest = async () => {
    setBusy(true);
    setErr(null);
    setResult(null);
    try {
      let input;
      try {
        input = JSON.parse(inputJson || '{}');
      } catch (e) {
        throw new Error(`Input JSON is invalid: ${e.message}`);
      }
      const res = await fetch(`${API}/api/policy/public-dry-run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rego_source: rego, input }),
      });
      const json = await res.json();
      if (!res.ok) {
        throw new Error(json.detail || `HTTP ${res.status}`);
      }
      setResult(json);
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setBusy(false);
    }
  };

  const verdict = result?.allow === true ? 'ALLOW' : result?.allow === false ? 'DENY' : null;

  return (
    <div className="dark min-h-screen bg-slate-950 text-slate-100 -mt-px">
      <SEOHead
        title="Rego Policy Playground — Try OPA-style FinOps Policies | CARTIEAI"
        description="Write and test Open Policy Agent (Rego) policies for cloud cost governance, tag hygiene, and LLM model allowlists. No signup required. Powered by CARTIEAI."
        canonical="/policy-playground"
        ogSlug="policy-playground"
      />
      <PublicNavbar />

      {/* Hero */}
      <div className="relative overflow-hidden border-b border-white/10">
        {/* atmospheric orbs */}
        <div className="absolute -top-32 -right-32 w-96 h-96 bg-emerald-200/40 blur-[120px] -z-10" />
        <div className="absolute -top-20 left-1/3 w-72 h-72 bg-emerald-200/40 blur-[120px] -z-10" />
        <div className="absolute top-40 -left-32 w-80 h-80 bg-teal-200/30 blur-[120px] -z-10" />

        <div className="max-w-6xl mx-auto px-6 pt-16 pb-10">
          <Badge className="bg-slate-900 text-emerald-300 border-0 font-mono uppercase tracking-[0.18em] mb-5 shadow-lg" data-testid="playground-badge">
            <ShieldCheck className="w-3 h-3 mr-1.5" /> Policy-as-Code · Public Playground
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-5 text-white leading-[1.05]" data-testid="playground-h1">
            Try <span className="text-emerald-400">Rego policies</span><br />
            for cloud cost governance.
          </h1>
          <p className="text-lg text-slate-300 max-w-3xl leading-relaxed font-medium">
            Open Policy Agent (Rego) is the industry standard for policy-as-code. CARTIE ships it natively for FinOps — gating cost-center tags, region allowlists, LLM model choices, EC2 sizes, and budget caps. <span className="text-white">Pick a template, hit Test, see the verdict.</span> Zero setup, no signup.
          </p>
        </div>
      </div>

      {/* Main playground */}
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="grid lg:grid-cols-[280px,1fr] gap-6">
          {/* Templates sidebar */}
          <div className="space-y-2">
            <div className="text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-2 flex items-center gap-1.5">
              <BookOpen className="w-3.5 h-3.5" /> Starter templates
            </div>
            {templates.length === 0 ? (
              <div className="text-xs text-slate-500 italic">Loading…</div>
            ) : templates.map(tpl => (
              <button
                key={tpl.template_id}
                onClick={() => pickTemplate(tpl)}
                data-testid={`template-${tpl.template_id}`}
                className={`w-full text-left p-3 rounded-lg border transition-all ${
                  selectedId === tpl.template_id
                    ? 'bg-emerald-500/15 border-emerald-400/50 shadow-md shadow-emerald-500/10'
                    : 'bg-slate-900/60 border-white/10 hover:border-emerald-500/40 hover:bg-emerald-500/5'
                }`}
              >
                <div className="flex items-start gap-2">
                  <FileCode className={`w-4 h-4 mt-0.5 ${selectedId === tpl.template_id ? 'text-emerald-600' : 'text-slate-400'}`} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-white truncate">{tpl.name}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">{tpl.description}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Editor + result */}
          <div className="space-y-4">
            <Card className="bg-slate-900/60 border-white/10 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2 text-white">
                  <Code2 className="w-4 h-4 text-emerald-400" />
                  Rego policy source
                </CardTitle>
              </CardHeader>
              <CardContent>
                <textarea
                  value={rego}
                  onChange={(e) => setRego(e.target.value)}
                  data-testid="rego-source-editor"
                  spellCheck={false}
                  className="w-full h-64 font-mono text-[12px] leading-snug p-3 bg-slate-950 text-emerald-200 rounded-md border border-white/10 focus:outline-none focus:border-emerald-500"
                  placeholder="package cartie.example..."
                />
              </CardContent>
            </Card>

            <Card className="bg-slate-900/60 border-white/10 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2 text-white">
                  <FileCode className="w-4 h-4 text-emerald-400" />
                  Input document (JSON)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <textarea
                  value={inputJson}
                  onChange={(e) => setInputJson(e.target.value)}
                  data-testid="rego-input-editor"
                  spellCheck={false}
                  className="w-full h-40 font-mono text-[12px] leading-snug p-3 bg-slate-900/40 text-slate-900 rounded-md border border-white/10 focus:outline-none focus:border-emerald-500"
                />
              </CardContent>
            </Card>

            <div className="flex items-center gap-3 flex-wrap">
              <Button
                onClick={runTest}
                disabled={busy || !rego.trim()}
                data-testid="run-test-btn"
                className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 shadow-md shadow-emerald-500/20 disabled:opacity-50"
              >
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
                Test policy
              </Button>
              <Button
                onClick={copyShareLink}
                disabled={!rego.trim()}
                variant="outline"
                data-testid="copy-share-link-btn"
                className="border-white/15 bg-white/5 text-slate-200 hover:border-emerald-400/60 hover:text-white hover:bg-emerald-500/15"
                title="Copy a permalink that pre-fills this exact policy + input on another machine"
              >
                {shareCopied ? <Check className="w-4 h-4 mr-2 text-emerald-600" /> : <Link2 className="w-4 h-4 mr-2" />}
                {shareCopied ? 'Link copied!' : 'Copy share link'}
              </Button>
              {verdict && (
                <Badge className={`text-sm font-bold font-mono tracking-wider ${verdict === 'ALLOW' ? 'bg-emerald-100 text-emerald-200 border-emerald-300' : 'bg-rose-100 text-rose-800 border-rose-300'}`} data-testid="verdict-badge">
                  {verdict === 'ALLOW' ? <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> : <XCircle className="w-3.5 h-3.5 mr-1" />}
                  {verdict}
                </Badge>
              )}
            </div>

            {err && (
              <Card className="bg-rose-500/10 border-rose-500/30 shadow-md" data-testid="run-error">
                <CardContent className="p-4 text-sm">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
                    <div>
                      <div className="font-semibold text-rose-900">Compile / eval error</div>
                      <pre className="text-rose-800 text-xs mt-1 whitespace-pre-wrap font-mono break-words">{err}</pre>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {result && !err && (
              <Card className="bg-slate-900/60 border-emerald-500/30 shadow-md shadow-emerald-500/10" data-testid="run-result">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2 text-white">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Result
                    <span className="text-[11px] text-slate-500 font-mono ml-auto">package {result.package}</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  {result.violations?.length > 0 && (
                    <div>
                      <div className="text-[11px] font-bold text-rose-300 uppercase tracking-widest mb-1.5">Violations ({result.violations.length})</div>
                      <ul className="space-y-1">
                        {result.violations.map((v, i) => (
                          <li key={i} className="text-rose-800 text-sm flex items-start gap-2">
                            <XCircle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-rose-500" /> {v}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {result.warnings?.length > 0 && (
                    <div>
                      <div className="text-[11px] font-bold text-amber-300 uppercase tracking-widest mb-1.5">Warnings ({result.warnings.length})</div>
                      <ul className="space-y-1">
                        {result.warnings.map((w, i) => (
                          <li key={i} className="text-amber-800 text-sm flex items-start gap-2">
                            <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-500" /> {w}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {(!result.violations || result.violations.length === 0) && (!result.warnings || result.warnings.length === 0) && (
                    <div className="text-emerald-300 text-sm flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4" /> Policy passed clean — no violations, no warnings.
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* CTA — gentle nudge to signup */}
        <div className="mt-12 p-6 rounded-2xl bg-slate-900 bg-gradient-to-r from-slate-900 via-slate-800 to-emerald-900 border border-emerald-500/30 shadow-xl shadow-emerald-500/10 text-white">
          <h2 className="text-2xl font-bold mb-2">Like it? Save your policies in CARTIE.</h2>
          <p className="text-slate-200 mb-4 max-w-2xl">
            Version, enable/disable, and evaluate policies against your live cloud and LLM spend. <span className="text-emerald-300 font-semibold">14-day pilot, no credit card, 3× ROI pledge or refund.</span>
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/login">
              <Button data-testid="playground-cta-signup" className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold shadow-lg shadow-emerald-500/30">
                Start free pilot →
              </Button>
            </Link>
            <Link to="/pricing">
              <Button variant="outline" data-testid="playground-cta-pricing" className="border-white/30 bg-white/10 text-white hover:bg-white/20">
                See pricing
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
